// Pure helpers for DataTable's column auto-fit (the DOM measuring lives in
// DataTable itself).

export const FIT_CAP = 300;
export const FIT_FLOOR = 60;

export interface FitColumn {
	id?: string;
	accessor?: unknown;
	width?: number;
	minWidth?: number;
	maxWidth?: number;
	columns?: FitColumn[];
	[key: string]: unknown;
}

/** react-table's column id: an explicit id, else a string accessor. */
export const columnId = (column: FitColumn): string | undefined =>
	column.id ?? (typeof column.accessor === "string" ? column.accessor : undefined);

/** Measured content width -> column width, within [FIT_FLOOR, FIT_CAP]. */
export const fitWidth = (measured: number) =>
	Math.min(FIT_CAP, Math.max(FIT_FLOOR, Math.ceil(measured)));

/** Columns only grow within a session, so paging through rows doesn't jiggle them. */
export const keepWidest = (previous: Record<string, number>, next: Record<string, number>) => {
	const merged = { ...previous };
	for (const [id, width] of Object.entries(next)) {
		merged[id] = Math.max(merged[id] ?? 0, width);
	}
	return merged;
};

/** Apply fitted widths as minWidth (react-table still flex-grows columns to fill),
 * raising any maxWidth below it. Fixed `width` columns are left alone. */
export const withFittedWidths = <T extends FitColumn>(columns: T[], fitted: Record<string, number>): T[] => {
	if (Object.keys(fitted).length === 0) return columns;
	return columns.map((column) => {
		if (column.columns) return { ...column, columns: withFittedWidths(column.columns, fitted) };
		const id = columnId(column);
		if (column.width !== undefined || id === undefined || fitted[id] === undefined) return column;
		const width = fitted[id];
		// a maxWidth that clips the column's own title/content defeats the fit
		const maxWidth = column.maxWidth !== undefined && column.maxWidth < width ? width : column.maxWidth;
		return maxWidth === undefined ? { ...column, minWidth: width } : { ...column, maxWidth, minWidth: width };
	});
};

export interface ResizedColumn {
	id: string;
	value: number;
}

/** react-table's `resized` list with one column's manual width set or replaced. */
export const withResized = (resized: ResizedColumn[], id: string, width: number): ResizedColumn[] => {
	const value = Math.ceil(width);
	return resized.some((r) => r.id === id)
		? resized.map((r) => (r.id === id ? { id, value } : r))
		: [...resized, { id, value }];
};
